
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navigation from '../../home/components/Navigation';
import Footer from '../../home/components/Footer';

export default function BeleuchtungPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleBack = () => {
    navigate('/');
    setTimeout(() => {
      const el = document.getElementById('leistungen');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const handleContactClick = () => {
    navigate('/');
    setTimeout(() => {
      const el = document.getElementById('kontakt');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
        window.dispatchEvent(new CustomEvent('preselect-service', { detail: 'Beleuchtung & Beschallung' }));
      }
    }, 100);
  };

  const features = [
    { icon: 'ri-lightbulb-flash-line', title: 'LED-Technologie', description: 'Energieeffiziente LED-Systeme für optimale Lichtausbeute und Kostenersparnis' },
    { icon: 'ri-remote-control-line', title: 'Smart Lighting', description: 'Intelligente Lichtsteuerung per App, Sprachbefehl oder Zeitschaltung' },
    { icon: 'ri-contrast-2-line', title: 'Akzentbeleuchtung', description: 'Stimmungsvolle Lichtkonzepte für besondere Atmosphäre in jedem Raum' },
    { icon: 'ri-sun-cloudy-line', title: 'Innen & Außen', description: 'Beleuchtungslösungen für Wohnräume, Büros, Geschäfte und Außenanlagen' },
  ];

  const benefits = [
    'Individuelle Lichtplanung',
    'Energieeffiziente LED-Systeme',
    'Smarte Lichtsteuerung',
    'Akzent- & Stimmungsbeleuchtung',
    'Innen- & Außenbeleuchtung',
    'Professionelle Installation',
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation isScrolled={isScrolled} />

      <div className="relative bg-gradient-to-br from-yellow-400 via-amber-500 to-orange-400 pt-24 sm:pt-32 pb-14 sm:pb-20 px-4 sm:px-6">
        <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/20"></div>
        <div className="max-w-4xl mx-auto relative z-10">
          <button
            onClick={handleBack}
            className="inline-flex items-center text-white/90 hover:text-white transition-colors cursor-pointer bg-transparent border-none p-0 mb-6 sm:mb-8 text-sm sm:text-base"
          >
            <i className="ri-arrow-left-line mr-2"></i>
            Zurück zu allen Leistungen
          </button>

          <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6 mb-4">
            <div className="w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center bg-white/20 backdrop-blur-sm rounded-2xl shrink-0">
              <i className="ri-lightbulb-line text-4xl sm:text-5xl text-white"></i>
            </div>
            <div>
              <h1 className="text-3xl sm:text-5xl font-bold text-white mb-1 sm:mb-2">Beleuchtung</h1>
              <p className="text-base sm:text-xl text-white/90">Durchdachte Lichtkonzepte für perfekte Atmosphäre</p>
            </div>
          </div>
        </div>
      </div>

      <main className="pb-16 sm:pb-20 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">

          <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 -mt-8 sm:-mt-10 relative z-20 mb-10 sm:mb-12">
            <p className="text-gray-700 leading-relaxed text-base sm:text-lg mb-4 sm:mb-6">
              Setzen Sie Ihre Räume perfekt in Szene mit durchdachten Beleuchtungskonzepten. Licht schafft Atmosphäre, steigert das Wohlbefinden und spart Energie.
              Wir planen und installieren moderne Beleuchtungslösungen für Innen- und Außenbereiche, die funktional und ästhetisch überzeugen.
            </p>
            <p className="text-gray-700 leading-relaxed text-base sm:text-lg">
              Von energieeffizienten LED-Systemen über smarte Lichtsteuerungen bis hin zu stimmungsvollen Akzentbeleuchtungen – wir realisieren individuelle Lichtkonzepte für jeden Bedarf.
              Mit der richtigen Beleuchtung schaffen wir die perfekte Lichtstimmung und sorgen gleichzeitig für optimale Energieeffizienz.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-10 sm:mb-12">
            {features.map((feature, index) => (
              <div key={index} className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-5 sm:p-6 border border-gray-100 hover:shadow-lg transition-shadow">
                <div className="w-11 h-11 sm:w-12 sm:h-12 flex items-center justify-center bg-gradient-to-br from-yellow-400 to-orange-400 rounded-lg mb-3 sm:mb-4">
                  <i className={`${feature.icon} text-xl sm:text-2xl text-white`}></i>
                </div>
                <h3 className="text-base sm:text-lg font-semibold text-[#1A2B4A] mb-2">{feature.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>

          <div className="bg-gradient-to-br from-[#1A2B4A] to-[#2A3B5A] rounded-2xl p-6 sm:p-8 mb-10 sm:mb-12 text-white">
            <h2 className="text-xl sm:text-2xl font-bold mb-5 sm:mb-6">Unsere Beleuchtungslösungen</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-[#F4B942] text-xl mr-3 mt-0.5 shrink-0"></i>
                  <span className="text-white/90 text-sm sm:text-base">{benefit}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-2xl p-8 sm:p-10 text-center">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1A2B4A] mb-3 sm:mb-4">
              Lassen Sie Ihre Räume erstrahlen
            </h2>
            <p className="text-gray-700 text-base sm:text-lg mb-6 sm:mb-8 max-w-2xl mx-auto">
              Kontaktieren Sie uns für eine kostenlose Lichtplanung und entdecken Sie die Möglichkeiten moderner Beleuchtung.
            </p>
            <button
              onClick={handleContactClick}
              className="inline-flex items-center justify-center w-full sm:w-auto px-8 py-4 bg-gradient-to-r from-yellow-400 to-orange-400 text-white font-semibold rounded-lg hover:shadow-xl transition-all whitespace-nowrap cursor-pointer border-none text-base"
            >
              Jetzt Anfrage senden
              <i className="ri-arrow-right-line ml-2"></i>
            </button>
          </div>

        </div>
      </main>
      <Footer />
    </div>
  );
}
